/*
 * DIE ERKLAERKARTE OBEN IM GUIDE EINES PARTNERS.
 *
 * Erste Fassung 23.09.2026 („Der Koenig und seine Berater", Rafaels Bild vom 10.09.).
 * Neu gefasst 05.10.2026 auf Rafaels Wort: „die erklaerung mit der koenig und seine
 * berater ist nicht mehr aktuell — der guide hat sich ja doch ein bisschen veraendert —
 * und es steht auch gar nicht was eine buehne alles kann, e.g. browser bedienen, apps
 * steuern und so weiter". Die Karte beschreibt deshalb, was der Partner heute sieht
 * (Reihen „Wartet auf dich" / „In Arbeit" / „Zurueckgestellt", die Knoepfe an der Buehne,
 * den Kanzler unten) und vor allem, was eine Buehne kann.
 *
 * Neu gefasst 25.10.2026: Abschnitt „Neu: die Ansicht nach Zielen“ (Umschalter, Alle, Reiter, Filter, Divers); die Reihen gelten jetzt als „in der Ansicht Räume“.
 * Der Stern „NEU“ am Umschalter oeffnet DIESE Karte (eine Erklaerung, nicht zwei) — siehe lib/guideErklaerkarteSteuerung.
 *
 * Partner-Sprache: Du-Form, keine Hof-Interna, keine Kennungen. Der Merker traegt eine
 * Fassung — wer die alte Karte weggeklickt hat, bekommt die neue einmal zu sehen.
 */
import { useEffect, useRef, useState } from 'react';
import { erklaerkarteMerkerFuer } from '../lib/guideVorgabe';
import { beiErklaerkarteOeffnen } from '../lib/guideErklaerkarteSteuerung';

export const ERKLAERKARTE_TITEL = 'So funktioniert dein Guide';

/** Fassung der Karte im Merker: neue Fassung = jeder sieht sie einmal neu. */
export const ERKLAERKARTE_FASSUNG = '2026-10-25';

/** Die neue Ansicht nach Zielen, in Partner-Sprache. */
export const ERKLAERKARTE_ZIELE: ReadonlyArray<readonly [string, string]> = [
  ['Umschalter', 'Oben wechselst du zwischen „Räume (bisher)“ und „Ziele (neu)“. Deine gewohnte Ansicht bleibt, es geht nichts verloren.'],
  ['Alle', 'Die Übersicht: ein Kästchen je Ziel, mit der Zahl der Bühnen und dem, was auf dich wartet. Ein Klick öffnet das Ziel.'],
  ['Reiter', 'Ein Reiter zeigt ein Ziel mit seinen Unterzielen und den Bühnen dazu.'],
  ['Filter', 'Wartet auf dich, Arbeitet, Pausiert, Ergebnis bereit: Jede Bühne trägt ihren Status, der Filter zeigt nur die passenden.'],
  ['Divers', 'Neue Bühnen ohne Ziel landen unter „Divers · noch einzuordnen“, bis sie zugeordnet sind.'],
];

/** Was eine Buehne kann — das Herzstueck der Karte. */
export const ERKLAERKARTE_BUEHNE_KANN: ReadonlyArray<readonly [string, string]> = [
  ['Echte Fenster statt Bilder', 'Apps, Webseiten und Dokumente laufen live auf der Bühne. Du klickst selbst darin herum, wie in deinem Browser.'],
  ['Schon angemeldet', 'Ein Fenster kann in einem eigenen Konto angemeldet sein, zum Beispiel einem Testkonto. Das ist getrennt von deinem Browser, nichts wird vermischt.'],
  ['Der Berater bedient für dich', 'Er öffnet die richtige Seite, füllt Formulare aus, klickt sich durch eine App und macht Aufnahmen. Du schaust zu und musst nicht suchen.'],
  ['Nebeneinander', 'Grundlage, App und Vergleich stehen gleichzeitig da. Du musst nicht zwischen Reitern wechseln.'],
  ['Fragen zum Anklicken', 'Was zu entscheiden ist, steht als Frage mit Antworten zum Anklicken da. Ein Klick genügt, eigene Sätze gehen immer.'],
];

export const ERKLAERKARTE_REIHEN: ReadonlyArray<readonly [string, string]> = [
  ['Wartet auf dich', 'Der Berater ist fertig. Bühnen mit einer Frage an dich stehen vorn.'],
  ['In Arbeit', 'Hier arbeitet ein Berater oder wartet auf Zuarbeit. Arbeitet gerade niemand, steht das an der Zeile.'],
  ['Zurückgestellt', 'Auf später gelegt, jederzeit einsehbar.'],
];

function istZu(userId: string): boolean {
  try { return localStorage.getItem(erklaerkarteMerkerFuer(userId)) === ERKLAERKARTE_FASSUNG; } catch { return false; }
}

function merke(userId: string, zu: boolean): void {
  try {
    if (zu) localStorage.setItem(erklaerkarteMerkerFuer(userId), ERKLAERKARTE_FASSUNG);
    else localStorage.removeItem(erklaerkarteMerkerFuer(userId));
  } catch (err) {
    console.warn('[Guide] Erklaerkarte: Zustand konnte nicht gemerkt werden:', err);
  }
}


export default function GuideErklaerkarte({ userId, onGesehen }: { userId: string; /** Wird gerufen, wenn die Karte zugeklappt wird (Verstanden/✕): dann gilt auch der Stern „NEU“ als gesehen. */ onGesehen?: () => void }) {
  const [zu, setZu] = useState(() => istZu(userId));
  const karte = useRef<HTMLElement>(null);
  const holeNach = useRef(false);
  /* Der Stern „NEU“ am Umschalter oeffnet diese Karte (auch wenn sie zugeklappt war) und bringt sie ins Bild. */
  useEffect(() => beiErklaerkarteOeffnen(() => { merke(userId, false); setZu(false); holeNach.current = true; }), [userId]);
  useEffect(() => {
    if (zu || !holeNach.current) return;
    holeNach.current = false;
    karte.current?.focus();
    karte.current?.scrollIntoView?.({ block: 'nearest' });
  }, [zu]);
  const schliesse = () => { merke(userId, true); setZu(true); onGesehen?.(); };

  if (zu) {
    return (
      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          data-ai-id="guide-erklaerkarte-oeffnen"
          onClick={() => { merke(userId, false); setZu(false); }}
          style={{ background: 'transparent', border: 0, padding: 0, fontSize: 11, color: 'var(--tn-gold)', cursor: 'pointer', textDecoration: 'underline' }}
        >◆ Wie funktioniert der Guide?</button>
      </div>
    );
  }

  return (
    <section
      ref={karte}
      tabIndex={-1}
      data-ai-id="guide-erklaerkarte"
      aria-label="So funktioniert dein Guide"
      style={{
        position: 'relative', marginBottom: 12, padding: '10px 12px', borderRadius: 8,
        border: '1px solid var(--tn-gold)', background: 'rgba(210,168,69,0.08)',
        color: 'var(--tn-text)', fontSize: 12.5, lineHeight: 1.5,
      }}
    >
      <button
        type="button"
        data-ai-id="guide-erklaerkarte-schliessen"
        aria-label="Erklärung schließen"
        title="Erklärung schließen"
        onClick={schliesse}
        style={{ position: 'absolute', top: 6, right: 8, background: 'transparent', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 14 }}
      >✕</button>
      <div style={{ fontWeight: 700, color: 'var(--tn-gold)', marginBottom: 6, paddingRight: 20 }}>◆ {ERKLAERKARTE_TITEL}</div>
      <p style={{ margin: '0 0 8px' }}>
        <b>Jede Bühne ist ein Thema.</b> Ein Berater bereitet sie für dich vor: links steht, worum es geht und was zu
        entscheiden ist, daneben die Fenster dazu. Du liest, schaust dir alles an und entscheidest.
      </p>
      <div style={{ fontWeight: 600, marginBottom: 2 }} data-ai-id="guide-erklaerkarte-neu-ziele">Neu: die Ansicht nach Zielen</div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        {ERKLAERKARTE_ZIELE.map(([was, text]) => (
          <li key={was}><b>{was}</b>: {text}</li>
        ))}
      </ul>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Was eine Bühne kann</div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        {ERKLAERKARTE_BUEHNE_KANN.map(([was, text]) => (
          <li key={was}><b>{was}</b>: {text}</li>
        ))}
      </ul>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Wo deine Bühnen stehen (in der Ansicht Räume)</div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        {ERKLAERKARTE_REIHEN.map(([reihe, text]) => (
          <li key={reihe}><b>{reihe}</b>: {text}</li>
        ))}
      </ul>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Was du tun kannst</div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        <li><b>„＋ Neue Bühne“</b> (oben): Schreib in einem Satz, worum es gehen soll. Ein Berater baut dir die Bühne.</li>
        <li><b>Dem Berater schreiben</b>: Unter jeder Bühne oder über „Chat mit Berater“, auch wenn gerade keine Frage dasteht.</li>
        <li><b>„✓ Erledigt“ und „Später“</b>: Du schließt dein Thema selbst ab oder legst es zurück.</li>
        <li><b>„Vorige Bühne“</b>: Zeigt, wie das Thema davor aussah.</li>
        <li><b>Dein Kanzler</b> (unten): Er ist dein Ansprechpartner für alles, auch für Fragen zum Guide.</li>
      </ul>
      <button
        type="button"
        data-ai-id="guide-erklaerkarte-verstanden"
        onClick={schliesse}
        style={{ fontSize: 11, padding: '4px 11px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--tn-gold)', background: 'var(--tn-gold)', color: 'var(--tn-bg)', fontWeight: 600 }}
      >Verstanden, ausblenden</button>
    </section>
  );
}
